// S3 activate dialog (§3.3.4, §9.1/§9.2). Effective-date picker (today or later),
// required activation note, and a live overlap preview mirroring the server's
// supersede decision ("Version 3 will be superseded effective …"). Blocks a
// past date and a same-or-earlier active overlap up front; the server remains
// authoritative and its problem is surfaced by the caller.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Button } from "../../../../../components/button";
import { isPastDate, previewSupersede, todayIso } from "./helpers";

type BomSummary = components["schemas"]["BomSummary"];

export function ActivateDialog({
  versions,
  draftFacilityId,
  activating,
  onActivate,
  onCancel,
}: {
  versions: readonly BomSummary[];
  draftFacilityId: string | null;
  activating: boolean;
  onActivate: (effectiveFrom: string, note: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const floor = todayIso();
  const [effectiveFrom, setEffectiveFrom] = useState(floor);
  const [note, setNote] = useState("");

  const past = isPastDate(effectiveFrom, floor);
  const noteMissing = note.trim().length === 0;
  const preview = previewSupersede(versions, draftFacilityId, effectiveFrom);
  const blocked = past || noteMissing || preview.conflict || effectiveFrom.length === 0;

  return (
    <div className="cat-scrim cat-scrim-center" role="presentation">
      {/* `.bom-dialog` is an M01-S07 selector. */}
      <div className="bom-dialog" role="dialog" aria-modal="true" aria-labelledby="activate-title">
        <div className="cat-modal-head">
          <h2 id="activate-title">{t("catalogue.bomEditor.activate.dialogTitle")}</h2>
        </div>

        <div className="cat-modal-body">
          <div className="bom-field">
            <label htmlFor="activate-date">{t("catalogue.bomEditor.activate.effectiveFrom")}</label>
            <input
              id="activate-date"
              type="date"
              min={floor}
              value={effectiveFrom}
              onChange={(e) => {
                setEffectiveFrom(e.target.value);
              }}
            />
            {past ? (
              <p className="field-error" role="alert">
                {t("catalogue.bomEditor.activate.pastDate")}
              </p>
            ) : null}
          </div>

          <div className="bom-field">
            <label htmlFor="activate-note">{t("catalogue.bomEditor.activate.note")}</label>
            <textarea
              id="activate-note"
              rows={3}
              value={note}
              placeholder={t("catalogue.bomEditor.activate.notePlaceholder")}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
            {noteMissing ? (
              <p className="bom-hint">{t("catalogue.bomEditor.activate.noteRequired")}</p>
            ) : null}
          </div>

          <div className="bom-overlap-preview" data-testid="overlap-preview">
            {preview.conflict && preview.supersededVersion !== null ? (
              <p className="field-error" role="alert">
                {t("catalogue.bomEditor.activate.conflictPreview", {
                  version: preview.supersededVersion,
                })}
              </p>
            ) : preview.supersededVersion !== null ? (
              <p className="bom-overlap-note">
                {t("catalogue.bomEditor.activate.overlapPreview", {
                  version: preview.supersededVersion,
                  date: effectiveFrom,
                })}
              </p>
            ) : (
              <p className="bom-hint">{t("catalogue.bomEditor.activate.noOverlap")}</p>
            )}
          </div>
        </div>

        <div className="cat-modal-foot">
          <div className="cat-modal-foot-right">
            <Button onClick={onCancel} disabled={activating}>
              {t("catalogue.bomEditor.activate.cancel")}
            </Button>
            <Button
              variant="primary"
              disabled={blocked}
              busy={activating}
              busyLabel={t("catalogue.bomEditor.activate.activating")}
              onClick={() => {
                onActivate(effectiveFrom, note.trim());
              }}
            >
              {t("catalogue.bomEditor.activate.confirm")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
